/**
 * ai-khung.ts — Phần THUẦN (không React) của module "Nhân viên AI" theo khung ba tầng
 * (docs/KE-HOACH-KHUNG-AI-3-TANG.md): điều hướng `/ai`, link cũ `?tab=`, nhân viên AI mặc định,
 * kênh nào đang có nhân viên AI trả lời, các bước của trang Bắt đầu, chế độ AI của hội thoại.
 *
 * Mọi trường mới của API (`isDefault`, `defaultMode`, `status`…) đều có thể chưa có (server cũ) → hàm ở đây
 * luôn có đường dự phòng hợp lý thay vì vỡ.
 */
import type { AiBot, AiMode } from '@/hooks/use-ai'
import { i18n } from '@/lib/i18n'
import { byokBroken, deriveWalletStatus, platformUsable, type AiCredits, type AiSources } from '@/lib/ai-credits'

// ── Đường dẫn ────────────────────────────────────────────────────────

export const AI_PATHS = {
  root: '/ai',
  start: '/ai/bat-dau',
  bots: '/ai/nhan-vien',
  /** Luồng tạo mới (agent sau làm màn riêng) — tạm mở hộp "Thêm nhân viên AI" hiện có. */
  newBot: '/ai/nhan-vien/moi',
  knowledge: '/ai/knowledge',
  library: '/ai/thu-vien',
  tracking: '/ai/theo-doi',
  sources: '/ai/nguon',
} as const

/** Màn một nhân viên AI. */
export const botPath = (id: string) => `/ai/nhan-vien/${id}`

// ── Theo dõi: tab theo hash ─────────────────────────────────────────

export type TrackingTab = 'nhat-ky' | 'cau-hoi' | 'kiem-dinh' | 'thong-ke'

export const TRACKING_TABS: Array<{ id: TrackingTab; label: string }> = [
  { id: 'nhat-ky', get label() { return i18n.t('aiTrainLib:aiKhung.trackingTab.log') } },
  { id: 'cau-hoi', get label() { return i18n.t('aiTrainLib:aiKhung.trackingTab.unanswered') } },
  { id: 'kiem-dinh', get label() { return i18n.t('aiTrainLib:aiKhung.trackingTab.eval') } },
  { id: 'thong-ke', get label() { return i18n.t('aiTrainLib:aiKhung.trackingTab.usage') } },
]

/** `#cau-hoi` → 'cau-hoi'; hash lạ / trống → tab đầu. */
export function trackingTabFromHash(hash: string | null | undefined): TrackingTab {
  const h = (hash ?? '').replace(/^#/, '')
  return TRACKING_TABS.some((t) => t.id === h) ? (h as TrackingTab) : 'nhat-ky'
}

// ── `/ai`: link cũ ?tab= và trang đích ──────────────────────────────

/** Tab của trang AI cũ → trang mới. */
const LEGACY_TAB_TARGET: Record<string, string> = {
  bots: AI_PATHS.bots,
  config: AI_PATHS.sources,
  usage: `${AI_PATHS.tracking}#thong-ke`,
  logs: `${AI_PATHS.tracking}#nhat-ky`,
  eval: `${AI_PATHS.tracking}#kiem-dinh`,
}

/** Giữ các tham số khác (vd `from=onboarding&step=3`) khi chuyển hướng. */
function withSearch(path: string, params: URLSearchParams): string {
  const qs = params.toString()
  if (!qs) return path
  const [base, hash] = path.split('#')
  return `${base}?${qs}${hash ? `#${hash}` : ''}`
}

/**
 * Link cũ `/ai?tab=…` → trang mới; null = không phải link cũ (để trang `/ai` tự chọn đích).
 * `tab=knowledge&category=<id>` → `/ai/knowledge?legacy=<id>` (kho tri thức mở đúng kho).
 */
export function legacyAiTabTarget(search: string | URLSearchParams): string | null {
  const params = new URLSearchParams(search)
  const tab = params.get('tab')
  if (!tab) return null
  params.delete('tab')
  if (tab === 'knowledge') {
    const cat = params.get('category')
    params.delete('category')
    if (cat) params.set('legacy', cat)
    return withSearch(AI_PATHS.knowledge, params)
  }
  const target = LEGACY_TAB_TARGET[tab]
  return target ? withSearch(target, params) : withSearch(AI_PATHS.bots, params)
}

/** Nhân viên AI có đang trả lời khách không: `status.answering` (server mới), không có thì theo công tắc bật. */
export function botAnswering(bot: Pick<AiBot, 'enabled' | 'status'>): boolean {
  if (bot.status && typeof bot.status.answering === 'boolean') return bot.status.answering
  return bot.enabled
}

/**
 * Trang đích của `/ai`: có nhân viên AI đang trả lời → danh sách nhân viên AI; chưa → Bắt đầu.
 * Server chưa trả `status` → chỉ dựa vào danh sách rỗng hay không.
 */
export function aiLandingPath(bots: Array<Pick<AiBot, 'enabled' | 'status'>> | null | undefined): string {
  if (!bots || bots.length === 0) return AI_PATHS.start
  const hasStatus = bots.some((b) => b.status && typeof b.status.answering === 'boolean')
  if (!hasStatus) return AI_PATHS.bots
  return bots.some((b) => b.status?.answering) ? AI_PATHS.bots : AI_PATHS.start
}

/** Đích cuối của `/ai?…`: link cũ trước, rồi theo tình trạng nhân viên AI; giữ tham số khác. */
export function aiIndexTarget(search: string, bots: Array<Pick<AiBot, 'enabled' | 'status'>> | null | undefined): string {
  return legacyAiTabTarget(search) ?? withSearch(aiLandingPath(bots), new URLSearchParams(search))
}

// ── Nhân viên AI mặc định ("Lễ tân AI", thay "AI chung") ────────────

/** Bot mặc định: `isDefault` (server mới), server cũ → bot "Tất cả tài khoản". */
export function isDefaultBot(bot: Pick<AiBot, 'isDefault' | 'allChannels'>): boolean {
  return typeof bot.isDefault === 'boolean' ? bot.isDefault : !!bot.allChannels
}

export function findDefaultBot<T extends Pick<AiBot, 'isDefault' | 'allChannels'>>(bots: T[] | null | undefined): T | undefined {
  return bots?.find(isDefaultBot)
}

/** Tên hiển thị thay chữ "AI chung"; không có bot mặc định → "AI chung" (dự phòng). */
export function defaultAiName(bots: Array<Pick<AiBot, 'isDefault' | 'allChannels' | 'name'>> | null | undefined): string {
  return findDefaultBot(bots)?.name?.trim() || i18n.t('aiTrainLib:aiKhung.defaultName')
}

// ── Kênh: nhân viên AI nào đang phụ trách ───────────────────────────

type BotLite = Pick<AiBot, 'id' | 'name' | 'enabled' | 'channelAccountIds' | 'allChannels' | 'isDefault' | 'status'>

/**
 * Nhân viên AI phụ trách một kênh (khớp `resolveBotForConversation`): bot đang bật gắn riêng kênh (bot đứng trước
 * thắng) → bot mặc định nếu đang bật → null.
 */
export function servingBotFor<T extends BotLite>(channelId: string, bots: T[]): { bot: T; viaDefault: boolean } | null {
  const own = bots.find((b) => b.enabled && (b.channelAccountIds ?? []).includes(channelId))
  if (own) return { bot: own, viaDefault: false }
  const def = bots.find((b) => isDefaultBot(b))
  return def && def.enabled ? { bot: def, viaDefault: true } : null
}

export interface ChannelLite {
  id: string
  displayName: string | null
  platform: number
  isDisabled?: boolean
}

export interface ChannelCoverage {
  channel: ChannelLite
  botId: string | null
  botName: string | null
  /** Kênh do nhân viên AI mặc định phụ trách (không giao riêng). */
  viaDefault: boolean
  /** Đang có nhân viên AI trả lời khách trên kênh này. */
  answering: boolean
}

/** Mỗi kênh đã kết nối: nhân viên AI nào phụ trách + có đang trả lời không. `paused` = tạm dừng toàn bộ AI. */
export function channelCoverage(channels: ChannelLite[], bots: BotLite[], opts: { paused?: boolean } = {}): ChannelCoverage[] {
  return channels
    .filter((c) => !c.isDisabled)
    .map((channel) => {
      // Server mới: `status.channels` = kênh bot THỰC SỰ phục vụ (khớp runtime) → tin nó trước; không có thì tự suy.
      const byStatus = bots.find((b) => b.status?.channels?.some((c) => c.id === channel.id))
      const s = byStatus ? { bot: byStatus, viaDefault: isDefaultBot(byStatus) } : servingBotFor(channel.id, bots)
      return {
        channel,
        botId: s?.bot.id ?? null,
        botName: s?.bot.name ?? null,
        viaDefault: s?.viaDefault ?? false,
        answering: !!s && !opts.paused && botAnswering(s.bot),
      }
    })
}

// ── Trang Bắt đầu: danh sách việc ───────────────────────────────────

/**
 * Nguồn AI chạy được chưa: ít nhất một nền tảng dùng AI của eCDP còn chạy (ví còn, chưa khoá) hoặc Key của tôi có key
 * không lỗi. null = server chưa có /ai/sources (không biết).
 */
export function aiSourceReady(
  sources: Pick<AiSources, 'providers' | 'platformEnabled' | 'orgBlocked'> | null | undefined,
  credits: Pick<AiCredits, 'balance' | 'monthlyGrant' | 'status'> | null | undefined,
): boolean | null {
  if (!sources) return null
  const wallet = credits ? deriveWalletStatus(credits) : null
  const platformOk = sources.platformEnabled !== false && !sources.orgBlocked && platformUsable(wallet)
  return sources.providers.some((p) =>
    p.source === 'platform' ? platformOk && p.platformAvailable : p.byok.configured && !byokBroken(p.byok))
}

export interface StartStep {
  key: 'channel' | 'bot' | 'knowledge' | 'assign' | 'source' | 'test' | 'live'
  title: string
  desc: string
  done: boolean
  /** Đường dẫn của nút. */
  to: string
  cta: string
  /** Bước không bắt buộc (không chặn "sẵn sàng"). */
  optional?: boolean
}

export interface StartInput {
  channels: number
  bots: BotLite[]
  coverage: ChannelCoverage[]
  /** Tạm dừng toàn bộ AI (autoReplyEnabled = false). undefined = chưa biết. */
  paused?: boolean
  /** Kho tri thức đã có tài liệu chưa (null = chưa biết). */
  knowledgeReady?: boolean | null
  /** Nguồn AI sẵn sàng (ví còn credit hoặc có key) — null = chưa biết. */
  sourceReady?: boolean | null
  /** Người xem sửa được Nguồn AI (owner/admin). */
  canEditSources?: boolean
}

/** Các bước để có nhân viên AI trả lời khách — thứ tự là thứ tự nên làm. */
export function startSteps(i: StartInput): StartStep[] {
  const firstBot = i.bots[0]
  const uncovered = i.coverage.filter((c) => !c.botId).length
  const steps: StartStep[] = [
    {
      key: 'channel', title: i18n.t('aiTrainLib:aiKhung.steps.channel.title'),
      desc: i.channels > 0 ? i18n.t('aiTrainLib:aiKhung.steps.channel.descDone', { count: i.channels }) : i18n.t('aiTrainLib:aiKhung.steps.channel.desc'),
      done: i.channels > 0, to: '/integrations', cta: i18n.t('aiTrainLib:aiKhung.steps.channel.cta'),
    },
    {
      key: 'bot', title: i18n.t('aiTrainLib:aiKhung.steps.bot.title'),
      desc: i.bots.length > 0 ? i18n.t('aiTrainLib:aiKhung.steps.bot.descDone', { count: i.bots.length }) : i18n.t('aiTrainLib:aiKhung.steps.bot.desc'),
      done: i.bots.length > 0, to: AI_PATHS.newBot, cta: i18n.t('aiTrainLib:aiKhung.steps.bot.cta'),
    },
    {
      key: 'knowledge', title: i18n.t('aiTrainLib:aiKhung.steps.knowledge.title'),
      desc: i18n.t('aiTrainLib:aiKhung.steps.knowledge.desc'),
      done: i.knowledgeReady === true, to: AI_PATHS.knowledge, cta: i18n.t('aiTrainLib:aiKhung.steps.knowledge.cta'),
    },
    {
      key: 'assign', title: i18n.t('aiTrainLib:aiKhung.steps.assign.title'),
      desc: i.channels === 0
        ? i18n.t('aiTrainLib:aiKhung.steps.assign.descNoChannel')
        : uncovered > 0 ? i18n.t('aiTrainLib:aiKhung.steps.assign.descUncovered', { count: uncovered }) : i18n.t('aiTrainLib:aiKhung.steps.assign.descAll'),
      done: i.channels > 0 && i.bots.length > 0 && uncovered === 0,
      to: firstBot ? botPath(firstBot.id) : AI_PATHS.bots, cta: i18n.t('aiTrainLib:aiKhung.steps.assign.cta'),
    },
  ]
  if (i.canEditSources) {
    steps.push({
      key: 'source', title: i18n.t('aiTrainLib:aiKhung.steps.source.title'),
      desc: i.sourceReady === false ? i18n.t('aiTrainLib:aiKhung.steps.source.descNotReady') : i18n.t('aiTrainLib:aiKhung.steps.source.desc'),
      done: i.sourceReady === true, to: AI_PATHS.sources, cta: i18n.t('aiTrainLib:aiKhung.steps.source.cta'),
      optional: i.sourceReady == null,
    })
  }
  steps.push(
    {
      key: 'test', title: i18n.t('aiTrainLib:aiKhung.steps.test.title'), desc: i18n.t('aiTrainLib:aiKhung.steps.test.desc'),
      done: false, to: firstBot ? botPath(firstBot.id) : AI_PATHS.bots, cta: i18n.t('aiTrainLib:aiKhung.steps.test.cta'), optional: true,
    },
    {
      key: 'live', title: i18n.t('aiTrainLib:aiKhung.steps.live.title'),
      desc: i.paused
        ? i18n.t('aiTrainLib:aiKhung.steps.live.descPaused')
        : i18n.t('aiTrainLib:aiKhung.steps.live.desc'),
      done: !i.paused && i.coverage.some((c) => c.answering),
      to: i.paused && i.canEditSources ? AI_PATHS.sources : AI_PATHS.bots, cta: i.paused ? i18n.t('aiTrainLib:aiKhung.steps.live.ctaResume') : i18n.t('aiTrainLib:aiKhung.steps.live.cta'),
    },
  )
  return steps
}

/** Chia bước: việc cần làm (bắt buộc trước, tuỳ chọn sau) + đã xong; `current` = việc nên làm ngay. */
export function groupStartSteps(steps: StartStep[]): { next: StartStep[]; done: StartStep[]; current: StartStep | null } {
  const todo = steps.filter((s) => !s.done)
  const next = [...todo.filter((s) => !s.optional), ...todo.filter((s) => s.optional)]
  return { next, done: steps.filter((s) => s.done), current: next.find((s) => !s.optional) ?? null }
}

// ── Hội thoại: chế độ AI ────────────────────────────────────────────

/** 'follow' = theo nhân viên AI (không có lựa chọn tay của nhân viên). */
export type ConvAiChoice = 'follow' | AiMode

export const CONV_AI_CHOICES: Array<{ value: ConvAiChoice; label: string; hint: string }> = [
  { value: 'follow', get label() { return i18n.t('aiTrainLib:aiKhung.convChoice.follow.label') }, get hint() { return i18n.t('aiTrainLib:aiKhung.convChoice.follow.hint') } },
  { value: 'manual', get label() { return i18n.t('aiTrainLib:aiKhung.convChoice.manual.label') }, get hint() { return i18n.t('aiTrainLib:aiKhung.convChoice.manual.hint') } },
  { value: 'suggest', get label() { return i18n.t('aiTrainLib:aiKhung.convChoice.suggest.label') }, get hint() { return i18n.t('aiTrainLib:aiKhung.convChoice.suggest.hint') } },
  { value: 'auto', get label() { return i18n.t('aiTrainLib:aiKhung.convChoice.auto.label') }, get hint() { return i18n.t('aiTrainLib:aiKhung.convChoice.auto.hint') } },
]

export const AI_MODE_LABEL: Record<AiMode, string> = {
  get manual() { return i18n.t('aiTrainLib:aiKhung.mode.manual') },
  get suggest() { return i18n.t('aiTrainLib:aiKhung.mode.suggest') },
  get auto() { return i18n.t('aiTrainLib:aiKhung.mode.auto') },
}

/** Lựa chọn tay của nhân viên (backend ghi `aiModeReason = 'set by staff …'`). */
export function isStaffChoice(reason: string | null | undefined): boolean {
  return !!reason && reason.startsWith('set by staff')
}

export function convAiChoice(conv: { aiMode: AiMode | string | null | undefined; aiModeReason?: string | null }): ConvAiChoice {
  if (isStaffChoice(conv.aiModeReason) && (conv.aiMode === 'manual' || conv.aiMode === 'suggest' || conv.aiMode === 'auto')) {
    return conv.aiMode
  }
  return 'follow'
}

/**
 * Thân PATCH /conversations/:id/ai-mode. "Theo nhân viên AI" = xoá lựa chọn tay → `aiMode: 'inherit'`
 * (giá trị mới đề xuất cho backend — xem báo cáo giao diện khung ba tầng).
 */
export function aiModeRequestBody(choice: ConvAiChoice): { aiMode: AiMode | 'inherit' } {
  return { aiMode: choice === 'follow' ? 'inherit' : choice }
}

/** Giờ (0–23, phút) theo múi giờ — dùng Intl như backend `isAfterHours`. */
function minutesIn(now: Date, timeZone: string): number {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', minute: 'numeric', hour12: false }).formatToParts(now)
    const h = Number(parts.find((p) => p.type === 'hour')?.value ?? 0) % 24
    const m = Number(parts.find((p) => p.type === 'minute')?.value ?? 0)
    return h * 60 + m
  } catch {
    return ((now.getUTCHours() + 7) % 24) * 60 + now.getUTCMinutes()
  }
}

/** Ngoài giờ? `start→end` là KHUNG NGOÀI GIỜ (mặc định 18→8), giống backend `isAfterHours`. */
export function isAfterHoursAt(now: Date, startHour = 18, endHour = 8, timeZone = 'Asia/Ho_Chi_Minh'): boolean {
  const t = minutesIn(now, timeZone)
  const s = startHour * 60
  const e = endHour * 60
  return s > e ? t >= s || t < e : t >= s && t < e
}

export type ConvModeSource = 'staff' | 'bot' | 'org-off' | 'no-bot' | 'bot-offline'

export interface EffectiveConvMode {
  mode: AiMode
  source: ConvModeSource
  botName: string | null
  /** Khung giờ đang áp khi theo lịch của nhân viên AI; 'all-day' = không bật lịch. */
  window: 'in-hours' | 'after-hours' | 'all-day' | null
  paused: boolean
}

type BotForMode = BotLite & Partial<Pick<AiBot,
  'scheduleEnabled' | 'scheduleStartHour' | 'scheduleEndHour' | 'scheduleDaytimeMode' | 'scheduleNighttimeMode' | 'defaultMode' | 'onlineStatus'>>

const asMode = (v: unknown, fallback: AiMode): AiMode => (v === 'manual' || v === 'suggest' || v === 'auto' ? v : fallback)

/**
 * Chế độ AI hiệu lực của một hội thoại LÚC NÀY (khung ba tầng §4): AI tạm dừng toàn bộ → Thủ công; nhân viên chọn
 * tay → theo đó; còn lại → lịch của nhân viên AI phụ trách kênh hoặc chế độ cả ngày của nó.
 */
export function effectiveConvMode(input: {
  conv: { aiMode: AiMode | string | null | undefined; aiModeReason?: string | null; aiPausedUntil?: string | null; channelAccountId?: string | null }
  bots: BotForMode[] | null | undefined
  /** false = Tạm dừng toàn bộ AI / AI tổ chức bị tắt. undefined = chưa biết (coi như bật). */
  aiOn?: boolean
  now?: Date
}): EffectiveConvMode {
  const now = input.now ?? new Date()
  const paused = !!input.conv.aiPausedUntil && new Date(input.conv.aiPausedUntil).getTime() > now.getTime()
  const served = input.conv.channelAccountId && input.bots ? servingBotFor(input.conv.channelAccountId, input.bots) : null
  const bot = served?.bot as BotForMode | undefined
  const botName = bot?.name ?? null
  if (input.aiOn === false) return { mode: 'manual', source: 'org-off', botName, window: null, paused }
  const choice = convAiChoice(input.conv)
  if (choice !== 'follow') return { mode: choice, source: 'staff', botName, window: null, paused }
  if (!bot) return { mode: 'manual', source: 'no-bot', botName: null, window: null, paused }
  if (bot.onlineStatus === 'offline') return { mode: 'manual', source: 'bot-offline', botName, window: null, paused }
  if (bot.scheduleEnabled) {
    const after = isAfterHoursAt(now, bot.scheduleStartHour ?? 18, bot.scheduleEndHour ?? 8)
    return after
      ? { mode: asMode(bot.scheduleNighttimeMode, 'auto'), source: 'bot', botName, window: 'after-hours', paused }
      : { mode: asMode(bot.scheduleDaytimeMode, 'suggest'), source: 'bot', botName, window: 'in-hours', paused }
  }
  return { mode: asMode(bot.defaultMode, 'suggest'), source: 'bot', botName, window: 'all-day', paused }
}

const WINDOW_TEXT: Record<NonNullable<EffectiveConvMode['window']>, string> = {
  get 'in-hours'() { return i18n.t('aiTrainLib:aiKhung.window.inHours') },
  get 'after-hours'() { return i18n.t('aiTrainLib:aiKhung.window.afterHours') },
  get 'all-day'() { return i18n.t('aiTrainLib:aiKhung.window.allDay') },
}

/** "theo Lễ tân AI · ngoài giờ" / "nhân viên chọn" / "AI đang tạm dừng toàn bộ"… */
export function effectiveSourceText(e: EffectiveConvMode): string {
  switch (e.source) {
    case 'staff': return i18n.t('aiTrainLib:aiKhung.source.staff')
    case 'org-off': return i18n.t('aiTrainLib:aiKhung.source.orgOff')
    case 'no-bot': return i18n.t('aiTrainLib:aiKhung.source.noBot')
    case 'bot-offline': return i18n.t('aiTrainLib:aiKhung.source.botOffline', { name: e.botName ?? i18n.t('aiTrainLib:aiKhung.botFallback') })
    case 'bot': return i18n.t('aiTrainLib:aiKhung.source.bot', { name: e.botName ?? i18n.t('aiTrainLib:aiKhung.botFallback'), window: e.window ? i18n.t('aiTrainLib:aiKhung.source.botWindow', { window: WINDOW_TEXT[e.window] }) : '' })
  }
}

/** "Tự động · theo Lễ tân AI · ngoài giờ" (+ "· đang tạm dừng"). */
export function effectiveModeText(e: EffectiveConvMode): string {
  return i18n.t('aiTrainLib:aiKhung.modeText', { mode: AI_MODE_LABEL[e.mode], source: effectiveSourceText(e), paused: e.paused ? i18n.t('aiTrainLib:aiKhung.modePaused') : '' })
}

// ── Thư mục dùng chung: phạm vi kỹ năng ───────────────────────────────────────

/** "Áp dụng cho": null/undefined = Tất cả; [] = chưa chọn; còn lại = tên nhân viên (bỏ id không còn). */
export function skillScopeLabel(botIds: string[] | null | undefined, bots: Array<Pick<AiBot, 'id' | 'name'>>): string {
  if (botIds == null) return i18n.t('aiTrainLib:aiKhung.scope.all')
  const names = botIds.map((id) => bots.find((b) => b.id === id)?.name).filter((n): n is string => !!n)
  if (names.length === 0) return i18n.t('aiTrainLib:aiKhung.scope.none')
  return names.join(', ')
}
